import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { notify } from "../../components/ui/notify";
import {
  Typography,
  Space,
  Button,
  Card,
  Modal,
  Spin,
  Row,
  Col,
  Input,
  Select,
  ConfigProvider,
} from "antd";
import { useDispatch } from "react-redux";
import axios from "axios";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { PATH } from "../../redux/utils";
import {
  actionAutoridadGetByCode,
  actionAutoridadUpdateByCode,
  actionAutoridadDeleteByCode,
} from "../../redux/actions/autoridades/autoridades";
import { HappyProvider } from "@ant-design/happy-work-theme";
import { showInsetEffect } from "../../utils/antdInsetWave";
import { DataBadge } from "../../components/ui";
import { usePermisos } from "../../context/PermisosContext";
import "../../assets/css/LaboralExpedienteDetallePage.css";
import "../../components/conciliacion/detalles/LaboralExpedienteDetallePage.css";
import "./materias/laboral/LaboralProcedimientoPage.css";
import "../../components/tribunal/TribunalPage.css";

dayjs.locale("es");

const { Text, Title } = Typography;

const STATUS_PILL_HEX = { primary: "#0A539A", gray: "#6B7280" };

const toTitleCase = (str) => {
  if (!str || typeof str !== "string") return "—";
  return str
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

const fmtDate = (d) => {
  if (!d) return "—";
  const x = dayjs(d);
  return x.isValid() ? x.format("DD/MM/YYYY") : "—";
};

const fmtDateLong = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" });
};

const api = axios.create({
  baseURL: PATH,
  headers: { "Content-Type": "application/json" },
});

const authHeader = () => {
  const token = localStorage.getItem("tokenadmin") || localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ===================== Info Tab =====================
function Field({ label, value }) {
  return (
    <div className="laboral-exp-field">
      <span className="laboral-exp-field-label">{label}</span>
      <span className="laboral-exp-field-value">{value ?? "—"}</span>
    </div>
  );
}

function InfoTab({ detalle, idAutoridad, dispatch, onUpdated, canEditar }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [ciudadesOptions, setCiudadesOptions] = useState([]);
  const [estadosOptions, setEstadosOptions] = useState([]);
  const [tiposOptions, setTiposOptions] = useState([]);
  const [form, setForm] = useState({ nombre: "", id_ciudad: null, id_estado: null, id_tipo_autoridad: null });

  useEffect(() => {
    if (!editing) return;
    api
      .get("ciudades", { headers: authHeader() })
      .then(({ data }) => {
        const arr = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
        setCiudadesOptions(arr.map((c) => ({ label: c.nombre, value: c.id_ciudad ?? c.id })));
      })
      .catch(() => {});
    api
      .get("estados", { headers: authHeader() })
      .then(({ data }) => {
        const arr = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
        setEstadosOptions(arr.map((e) => ({ label: e.nombre, value: e.id_estado ?? e.id })));
      })
      .catch(() => {});
    api
      .get("tipo_autoridades", { headers: authHeader() })
      .then(({ data }) => {
        const arr = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
        setTiposOptions(arr.map((t) => ({ label: t.nombre, value: t.id_tipo_autoridad ?? t.id })));
      })
      .catch(() => {});
  }, [editing]);

  const startEdit = () => {
    setForm({
      nombre: detalle.nombre_autoridad || detalle.nombre || "",
      id_ciudad: detalle.id_ciudad ?? null,
      id_estado: detalle.id_estado ?? null,
      id_tipo_autoridad: detalle.id_tipo_autoridad ?? null,
    });
    setEditing(true);
  };

  const handleSave = async () => {
    if (!String(form.nombre || "").trim()) {
      notify.warning({ message: "El nombre es requerido", placement: "bottomRight" });
      return;
    }
    try {
      setSaving(true);
      await dispatch(
        actionAutoridadUpdateByCode(idAutoridad, {
          nombre: form.nombre.trim(),
          id_ciudad: form.id_ciudad ?? null,
          id_estado: form.id_estado ?? null,
          id_tipo_autoridad: form.id_tipo_autoridad ?? null,
        })
      );
      notify.success({ message: "Autoridad actualizada", placement: "bottomRight" });
      setEditing(false);
      await onUpdated?.();
    } catch (e) {
      notify.error({
        message: "No se pudo guardar",
        description: e?.response?.data?.detail || e?.message,
        placement: "bottomRight",
      });
    } finally {
      setSaving(false);
    }
  };

  const nombreActual = detalle.nombre_autoridad || detalle.nombre;

  return (
    <Card className="laboral-exp-detail-card laboral-exp-detail-card-main p-2" style={{ marginTop: 18 }}>
      <div className="laboral-exp-card-header" style={{ justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div className="laboral-exp-icon-badge">
            <i className="fa-solid fa-landmark" />
          </div>
          <h3 className="laboral-exp-card-header-title">Datos de la autoridad</h3>
        </div>

        {canEditar && !editing && (
          <Button onClick={startEdit} className="laboral-exp-btn-clean">
            Editar
          </Button>
        )}
      </div>

      {editing ? (
        <div className="laboral-exp-edit-form">
          <div className="laboral-exp-edit-grid">
            <div className="laboral-exp-edit-field">
              <span className="laboral-exp-edit-label">Nombre</span>
              <Input
                value={form.nombre}
                onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))}
              />
            </div>

            <div className="laboral-exp-edit-field">
              <span className="laboral-exp-edit-label">Tipo de autoridad</span>
              <Select
                showSearch
                optionFilterProp="label"
                allowClear
                style={{ width: "100%" }}
                options={tiposOptions}
                value={form.id_tipo_autoridad}
                onChange={(v) => setForm((f) => ({ ...f, id_tipo_autoridad: v ?? null }))}
              />
            </div>

            <div className="laboral-exp-edit-field">
              <span className="laboral-exp-edit-label">Estado</span>
              <Select
                showSearch
                optionFilterProp="label"
                allowClear
                style={{ width: "100%" }}
                options={estadosOptions}
                value={form.id_estado}
                onChange={(v) => setForm((f) => ({ ...f, id_estado: v ?? null }))}
              />
            </div>

            <div className="laboral-exp-edit-field">
              <span className="laboral-exp-edit-label">Ciudad</span>
              <Select
                showSearch
                optionFilterProp="label"
                allowClear
                style={{ width: "100%" }}
                options={ciudadesOptions}
                value={form.id_ciudad}
                onChange={(v) => setForm((f) => ({ ...f, id_ciudad: v ?? null }))}
              />
            </div>
          </div>

          <div className="laboral-exp-edit-actions">
            <Button onClick={() => setEditing(false)} className="laboral-exp-btn-clean" disabled={saving}>
              Cancelar
            </Button>
            <HappyProvider>
              <Button
                type="primary"
                loading={saving}
                onClick={handleSave}
                className="laboral-exp-btn-save"
                icon={<i className="fa-solid fa-floppy-disk" />}
              >
                Guardar
              </Button>
            </HappyProvider>
          </div>
        </div>
      ) : (
        <div className="laboral-exp-field-grid">
          <Field label="Nombre" value={toTitleCase(nombreActual)} />
          <Field label="Tipo de autoridad" value={detalle.nombre_tipo_autoridad || "—"} />
          <Field label="Estado" value={detalle.nombre_estado || "—"} />
          <Field label="Ciudad" value={detalle.nombre_ciudad || "—"} />
          <Field label="Estatus" value={Number(detalle.active) === 1 ? "Activa" : "Inactiva"} />
          <Field label="Fecha de registro" value={fmtDate(detalle.created_at)} />
        </div>
      )}
    </Card>
  );
}

// ===================== Main Page =====================
export default function AutoridadDetallePage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { idAutoridad } = useParams();

  const { perm } = usePermisos() || { perm: () => true };
  const canEditar = perm("autoridades", "editar");
  const canEliminar = perm("autoridades", "eliminar");

  const [detalle, setDetalle] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const reloadDetalle = useCallback(async () => {
    try {
      setLoadingDetalle(true);
      const data = await dispatch(actionAutoridadGetByCode(idAutoridad));
      setDetalle(data || null);
    } catch {
      notify.error({
        message: "Error al cargar",
        description: "No se pudo obtener el detalle de la autoridad.",
      });
      setDetalle(null);
    } finally {
      setLoadingDetalle(false);
    }
  }, [dispatch, idAutoridad]);

  useEffect(() => {
    reloadDetalle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, idAutoridad]);

  const handleDelete = () => {
    Modal.confirm({
      title: "Eliminar autoridad",
      content: "Esta acción eliminará la autoridad. ¿Deseas continuar?",
      okText: "Eliminar",
      okType: "danger",
      cancelText: "Cancelar",
      centered: true,
      okButtonProps: { loading: deleting, disabled: deleting },
      cancelButtonProps: { disabled: deleting },
      onOk: async () => {
        try {
          setDeleting(true);
          await dispatch(actionAutoridadDeleteByCode(idAutoridad));
          notify.success({ message: "Autoridad eliminada", description: "Se eliminó correctamente." });
          navigate("/autoridades");
        } catch (error) {
          const msg = error?.response?.data?.detail || error?.message || "No se pudo eliminar la autoridad.";
          notify.error({ message: "Error al eliminar", description: msg });
        } finally {
          setDeleting(false);
        }
      },
    });
  };

  if (loadingDetalle || !detalle) {
    return (
      <div className="lxp-root">
        <div className="lxp-wrap">
          <Card style={{ marginTop: 16, borderRadius: 12 }}>
            {loadingDetalle ? (
              <div style={{ padding: 40, textAlign: "center" }}>
                <Spin size="large" />
              </div>
            ) : (
              <Text type="secondary">Sin datos de la autoridad.</Text>
            )}
          </Card>
        </div>
      </div>
    );
  }

  const nombreActual = detalle.nombre_autoridad || detalle.nombre;
  const estatusPill = Number(detalle.active) === 1
    ? { label: "ACTIVA", kind: "primary" }
    : { label: "INACTIVA", kind: "gray" };

  return (
    <div className="lxp-root">
      <div className="lxp-wrap">
        {/* Summary card */}
        <div className="laboral-exp-main" style={{ background: "transparent", padding: 0, minHeight: "auto" }}>
        <ConfigProvider wave={{ showEffect: showInsetEffect }}>
        <Card className="laboral-exp-summary-card p-2">
          <Row align="middle" gutter={[16, 16]} justify="space-between" wrap>
            <Col flex="auto">
              <Space direction="vertical" size={8}>
                <div className="laboral-exp-summary-title">
                  <Space align="center" wrap>
                    <Title
                      level={4}
                      style={{
                        margin: 0,
                        fontSize: 28,
                        fontWeight: 650,
                        letterSpacing: "-0.01em",
                        lineHeight: 1.2,
                        color: "#0f172a",
                      }}
                    >
                      {toTitleCase(nombreActual).toUpperCase()}
                    </Title>
                    <DataBadge
                      color={STATUS_PILL_HEX[estatusPill.kind] || estatusPill.kind}
                      className="d-inline-flex align-items-center gap-1 status-pill-badge"
                    >
                      {estatusPill.label}
                    </DataBadge>
                  </Space>
                  <Text type="secondary" className="laboral-exp-subtitle">
                    {detalle.nombre_tipo_autoridad || "Sin tipo asignado"}
                  </Text>
                </div>

                <Space size="large" wrap className="laboral-exp-summary-meta">
                  <Space className="laboral-exp-meta-item">
                    <i className="fa-solid fa-location-dot" />
                    <Text>Ciudad: {detalle.nombre_ciudad || "—"}, Estado: {detalle.nombre_estado || "—"}</Text>
                  </Space>
                  <Space className="laboral-exp-meta-item">
                    <i className="fa-solid fa-calendar-days" />
                    <Text>Creado: {fmtDateLong(detalle.created_at)}</Text>
                  </Space>
                </Space>
              </Space>
            </Col>

            <Col flex="none">
              <Space>
                {canEliminar && (
                <Button
                  danger
                  type="primary"
                  icon={<i className="fa-solid fa-trash" />}
                  style={{ borderRadius: 8 }}
                  onClick={handleDelete}
                  loading={deleting}
                  disabled={deleting}
                >
                  Eliminar
                </Button>
                )}
              </Space>
            </Col>
          </Row>
        </Card>
        </ConfigProvider>
        </div>

        <InfoTab detalle={detalle} idAutoridad={idAutoridad} dispatch={dispatch} onUpdated={reloadDetalle} canEditar={canEditar} />
      </div>
    </div>
  );
}
